// src/Contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithPopup, 
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  onSnapshot,
  collection,
  addDoc 
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, googleProvider, db, functions } from '../firebase';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);

  useEffect(() => {
    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      
      if (currentUser) {
        // Create user document if it doesn't exist
        await setDoc(
          doc(db, 'customers', currentUser.uid),
          {
            email: currentUser.email,
            displayName: currentUser.displayName,
            photoURL: currentUser.photoURL,
          },
          { merge: true }
        );
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Listen to subscription changes
  useEffect(() => {
    if (!user) {
      setIsPremium(false);
      setSubscriptionData(null);
      return;
    }

    // Listen to the subscriptions subcollection
    const subscriptionsRef = collection(db, 'customers', user.uid, 'subscriptions');
    
    const unsubscribe = onSnapshot(subscriptionsRef, (snapshot) => {
      let hasActiveSubscription = false;
      let latestSubscription = null;

      snapshot.forEach((doc) => {
        const subscription = doc.data();
        // Check if subscription is active
        if (subscription.status === 'active' || subscription.status === 'trialing') {
          hasActiveSubscription = true;
          latestSubscription = { id: doc.id, ...subscription };
        }
      });

      setIsPremium(hasActiveSubscription);
      setSubscriptionData(latestSubscription);
    });

    return unsubscribe;
  }, [user]);

  const signInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      return result.user;
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const createCheckoutSession = async (priceId = "price_1SSrMCErlAYfDta7nMEsqyCO") => {
    if (!user) throw new Error('User must be logged in');

    try {
      // Create a checkout session document
      const checkoutSessionRef = collection(
        db,
        'customers',
        user.uid,
        'checkout_sessions'
      );

      const docRef = await addDoc(checkoutSessionRef, {
        price: priceId,
        success_url: window.location.origin,
        cancel_url: window.location.origin,
        mode: 'subscription',
      });

      // Wait for the checkout session URL
      return new Promise((resolve, reject) => {
        const unsubscribe = onSnapshot(docRef, (snap) => {
          const data = snap.data();
          
          if (data?.error) {
            unsubscribe();
            reject(new Error(data.error.message));
          }
          
          if (data?.url) {
            unsubscribe();
            // Redirect to Stripe Checkout
            window.location.assign(data.url);
            resolve(data.url);
          }
        });
      });
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  };

  const createPortalLink = async () => {
    if (!user) throw new Error('User must be logged in');

    try {
      // The extension creates a special collection for portal sessions
      const functionRef = httpsCallable(
        functions, 
        'ext-firestore-stripe-payments-yvxl-createPortalLink'
      );
      
      const { data } = await functionRef({ 
        returnUrl: window.location.origin,
        locale: 'auto',
        customer: user.uid
      });
      
      if (data && data.url) {
        return data.url;
      } else {
        throw new Error('No portal URL returned');
      }
    } catch (error) {
      console.error('Error creating portal link:', error);
      
      // Fallback: Try to open portal using Firestore method
      console.log('Trying fallback method...');
      try {
        const docRef = await addDoc(
          collection(db, 'customers', user.uid, 'checkout_sessions'),
          {
            returnUrl: window.location.origin,
            locale: 'auto',
          }
        );

        return new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            unsubscribe();
            reject(new Error('Timeout waiting for portal URL'));
          }, 15000);

          const unsubscribe = onSnapshot(docRef, (snap) => {
            const data = snap.data();
            
            if (data?.error) {
              clearTimeout(timeout);
              unsubscribe();
              reject(new Error(data.error.message));
            }
            
            if (data?.url) {
              clearTimeout(timeout);
              unsubscribe();
              resolve(data.url);
            }
          });
        });
      } catch (fallbackError) {
        throw new Error('Unable to create portal link. Please try again later.');
      }
    }
  };

  const value = {
    user,
    loading,
    isPremium,
    subscriptionData,
    signInWithGoogle,
    logout,
    createCheckoutSession,
    createPortalLink,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}